<template>
  <v-dialog v-model="showDialog" max-width="600">
    <v-card>
      <v-card-title>{{ isEditMode ? 'Edit List' : 'Create New List' }}</v-card-title>
      <v-card-text>
        <v-form ref="listForm" v-model="listFormValid" @submit.prevent="submitForm">
          <v-text-field
            v-model="listData.name"
            label="Name"
            :rules="[v => !!v || 'Name is required']"
            required
            @keydown.enter.prevent="submitForm"
          ></v-text-field>
          <v-textarea
            v-model="listData.description"
            label="Description (optional)"
            rows="3"
            @keydown.enter.prevent="submitForm"
          ></v-textarea>
          <v-text-field
            v-model="listData.duration"
            label="Duration (days)"
            type="number"
            min="1"
            @keydown.enter.prevent="submitForm"
          ></v-text-field>

          <!-- Current Header Image Display -->
          <div v-if="isEditMode && listData.header_image_url" class="mb-4">
            <v-card variant="outlined">
              <v-card-title class="text-subtitle-1 d-flex align-center justify-space-between">
                Current Header Image
                <v-btn
                  color="error"
                  variant="outlined"
                  size="small"
                  prepend-icon="mdi-delete"
                  @click="removeHeaderImage"
                  :disabled="isProcessing"
                >
                  Remove Image
                </v-btn>
              </v-card-title>
              <v-img
                :src="getAuthenticatedImageUrl(listData.header_image_url)"
                :alt="`${listData.name} header image`"
                max-height="200"
                max-width="100%"
                contain
                class="mx-2 mb-2"
              >
                <template #error>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-icon>mdi-image-broken-variant</v-icon>
                    <span class="ml-2">Failed to load image</span>
                  </v-row>
                </template>
              </v-img>
            </v-card>
          </div>

          <!-- AI Header Image Generation -->
          <div>
            <v-checkbox
              v-model="listData.generateHeaderImage"
              :label="(isEditMode && listData.header_image_url) ? 'Regenerate AI header image for this list' : 'Generate AI header image for this list'"
              density="compact"
              class="mt-2"
              :hint="(isEditMode && listData.header_image_url) ? 'Check to replace the current header image with a new one' : 'Check to enable header image generation for this list'"
              persistent-hint
            ></v-checkbox>
            
            <div v-if="listData.generateHeaderImage">
              <v-textarea
                v-model="listData.headerImagePrompt"
                label="Header image generation prompt"
                rows="3"
                variant="outlined"
                class="mt-2"
                hint="This prompt will be used to generate an AI header image for the list"
                persistent-hint
              ></v-textarea>

              <v-select
                v-model="listData.headerImageSize"
                label="Image size"
                :items="imageSizeOptions"
                variant="outlined"
                class="mt-2"
              ></v-select>
              
              <v-btn
                color="secondary"
                variant="outlined"
                prepend-icon="mdi-image-plus"
                @click="generateHeaderImage"
                :disabled="!listData.headerImagePrompt.trim() || isGeneratingImage"
                :loading="isGeneratingImage"
                class="mt-2"
              >
                {{ (isEditMode && listData.header_image_url) ? 'Regenerate Header Image' : 'Generate Header Image' }}
              </v-btn>

              <!-- Image Preview -->
              <div v-if="previewImageUrl" class="mt-4">
                <v-card variant="outlined">
                  <v-card-title class="text-subtitle-2">
                    <v-icon icon="mdi-image" class="me-2"></v-icon>
                    Generated Header Image Preview
                    <v-spacer></v-spacer>
                    <v-btn
                      icon="mdi-close"
                      size="small"
                      variant="text"
                      @click="clearPreviewImage"
                    ></v-btn>
                  </v-card-title>
                  <v-card-text class="pa-2">
                    <img 
                      :src="previewImageUrl" 
                      :alt="listData.headerImagePrompt"
                      style="width: 100%; height: auto; border-radius: 4px;"
                    />
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="cancel" :disabled="isProcessing">Cancel</v-btn>
        <v-btn 
          ref="submitButton" 
          color="primary" 
          @click="submitForm" 
          :disabled="!listFormValid || isProcessing || isGeneratingImage"
          :loading="isProcessing"
        >
          {{ isEditMode ? 'Update' : 'Create List' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useListStore } from '../stores/list'
import { useImageAuthentication } from '../composables/useImageAuthentication'
import { useRouter } from 'vue-router'
import axios from 'axios'

export default {
  name: 'ListDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    initialData: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue', 'created', 'updated', 'message'],
  setup(props, { emit }) {
    const listStore = useListStore()
    const router = useRouter()
    
    // Add image authentication composable (for regular authenticated users)
    const { getAuthenticatedImageUrl } = useImageAuthentication(
      ref(false), // not shared
      ref(null),  // no link_id
      ref(null),  // no hmac
      ref(null)   // no authenticated password
    )
    
    const listForm = ref(null)
    const listFormValid = ref(false)
    const isProcessing = ref(false)
    const isGeneratingImage = ref(false)
    const previewImageUrl = ref(null)
    const previewImageDataUri = ref(null)
    
    const imageSizeOptions = [
      { title: 'Full (832x1280)', value: 'full' },
      { title: 'Compact (1536x384)', value: 'compact' },
      { title: 'Slim (1536x192)', value: 'slim' }
    ]
    
    const listData = ref({
      id: null,
      name: '',
      description: '',
      duration: 1,
      header_image_url: '',
      generateHeaderImage: false,
      headerImagePrompt: '',
      headerImageSize: 'full'
    })
    
    const isEditMode = computed(() => {
      return props.initialData && props.initialData.id
    })
    
    const showDialog = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })
    
    const showMessage = (message, type = 'info') => {
      emit('message', { message, type })
    }

    const resetForm = () => {
      listData.value = {
        id: null,
        name: '',
        description: '',
        duration: 1,
        header_image_url: '',
        generateHeaderImage: false,
        headerImagePrompt: '',
        headerImageSize: 'full'
      }
      clearPreviewImage()
    }
    
    const removeHeaderImage = async () => {
      if (!isEditMode.value) return
      
      if (!confirm('Are you sure you want to remove the header image from this list?')) return
      
      isProcessing.value = true
      
      try {
        const updateData = {
          name: listData.value.name,
          description: listData.value.description,
          duration: listData.value.duration,
          header_image_url: null
        }
        
        const result = await listStore.updateList(listData.value.id, updateData)
        if (result.success) {
          listData.value.header_image_url = null
          showMessage('Header image removed successfully!')
          emit('updated')
        } else {
          showMessage(result.error || 'Failed to remove header image', 'error')
        }
      } catch (error) {
        showMessage('Error removing header image', 'error')
      } finally {
        isProcessing.value = false
      }
    }
    
    const generateHeaderImage = async () => {
      if (!listData.value.headerImagePrompt.trim()) return
      
      isGeneratingImage.value = true
      
      try {
        if (isEditMode.value) {
          // Update existing list with image generation
          const updateData = {
            generate_header_image: true,
            header_image_prompt: listData.value.headerImagePrompt,
            header_image_size: listData.value.headerImageSize
          }
          
          const result = await listStore.updateList(listData.value.id, updateData)
          if (result.success) {
            // Refresh the list to get the updated header image
            const refreshResult = await listStore.fetchList(listData.value.id)
            if (refreshResult.success && refreshResult.list?.header_image_url) {
              listData.value.header_image_url = refreshResult.list.header_image_url
              previewImageUrl.value = getAuthenticatedImageUrl(refreshResult.list.header_image_url)
              showMessage('AI header image generated successfully!')
              emit('updated')
            } else {
              showMessage('Header image generation failed', 'error')
            }
          } else {
            showMessage('Header image generation failed', 'error')
          }
        } else {
          // For create mode, generate preview image
          const formData = new FormData()
          formData.append('header_image_prompt', listData.value.headerImagePrompt)
          formData.append('header_image_size', listData.value.headerImageSize)
          
          const response = await axios.post('/api/generate-header-image', formData)
          
          if (response.data.success) {
            previewImageDataUri.value = response.data.image_data_uri
            previewImageUrl.value = response.data.image_data_uri
            showMessage('AI header image generated successfully!')
          } else {
            showMessage('Header image generation failed', 'error')
          }
        }
      } catch (error) {
        console.error('Header image generation failed:', error)
        showMessage('Header image generation failed', 'error')
      } finally {
        isGeneratingImage.value = false
      }
    }

    const clearPreviewImage = () => {
      previewImageUrl.value = null
      previewImageDataUri.value = null
    }
    
    const submitForm = async () => {
      if (!listFormValid.value) return
      
      isProcessing.value = true
      
      const formData = {
        name: listData.value.name,
        description: listData.value.description,
        duration: listData.value.duration
      }

      // Add header image generation if enabled (only if no preview image exists)
      if (listData.value.generateHeaderImage && listData.value.headerImagePrompt.trim() && !previewImageDataUri.value) {
        formData.generate_header_image = true
        formData.header_image_prompt = listData.value.headerImagePrompt
        formData.header_image_size = listData.value.headerImageSize
      }
      
      try {
        if (isEditMode.value) {
          // Update existing list
          const result = await listStore.updateList(listData.value.id, formData)
          if (result.success) {
            showMessage('List updated successfully!')
            emit('updated')
            cancel()
          } else {
            showMessage(result.error || 'Failed to update list', 'error')
          }
        } else {
          // Create new list - always use FormData now
          const createFormData = new FormData()
          createFormData.append('name', listData.value.name)
          createFormData.append('description', listData.value.description || '')
          createFormData.append('duration', listData.value.duration.toString())
          
          // Add image generation fields
          if (listData.value.generateHeaderImage && listData.value.headerImagePrompt.trim()) {
            createFormData.append('generate_header_image', 'true')
            createFormData.append('header_image_prompt', listData.value.headerImagePrompt)
            createFormData.append('header_image_size', listData.value.headerImageSize)
          }
          
          // Add pre-generated image if available
          if (previewImageDataUri.value) {
            createFormData.append('header_image_data_uri', previewImageDataUri.value)
          }
          
          try {
            const response = await axios.post('/api/packlists', createFormData)
            showMessage('List created successfully!')
            emit('created', response.data)
            
            // Navigate to the new list
            router.push(`/lists/${response.data.id}`)
            cancel()
          } catch (error) {
            console.error('Create list error:', error)
            const errorMessage = error.response?.data?.detail || 'Failed to create list'
            showMessage(errorMessage, 'error')
          }
        }
      } catch (error) {
        showMessage('Error processing request', 'error')
      } finally {
        isProcessing.value = false
      }
    }
    
    const cancel = () => {
      resetForm()
      showDialog.value = false
    }
    
    // Watch for initialData prop changes to populate the form
    watch(() => props.initialData, (newData) => {
      if (newData && newData.id) {
        // Edit mode
        listData.value = {
          id: newData.id,
          name: newData.name,
          description: newData.description || '',
          duration: newData.duration || 1,
          header_image_url: newData.header_image_url || '',
          generateHeaderImage: false,
          headerImagePrompt: '',
          headerImageSize: 'full'
        }
      } else if (props.modelValue) {
        // Create mode - reset form when dialog opens
        resetForm()
      }
    }, { immediate: true })
    
    // Watch for generateHeaderImage checkbox to set default prompt
    watch(() => listData.value.generateHeaderImage, (newVal) => {
      if (newVal && !listData.value.headerImagePrompt.trim()) {
        const listName = listData.value.name || 'list'
        listData.value.headerImagePrompt = listName
      }
    })
    
    // Update header image prompt when list name changes if image generation is enabled
    watch(() => listData.value.name, (newVal) => {
      if (listData.value.generateHeaderImage && newVal) {
        listData.value.headerImagePrompt = newVal
      }
    })
    
    return {
      showDialog,
      listForm,
      listFormValid,
      isProcessing,
      isGeneratingImage,
      previewImageUrl,
      listData,
      isEditMode,
      imageSizeOptions,
      getAuthenticatedImageUrl,
      removeHeaderImage,
      generateHeaderImage,
      clearPreviewImage,
      submitForm,
      cancel
    }
  }
}
</script>